import React from 'react';
import { ArticleLayout } from '@/components/ArticleLayout';

const content = `
Mars 2024. Un avocat parisien reçoit une convocation du Conseil de l'Ordre. Motif : violation potentielle du secret professionnel. Il avait utilisé ChatGPT pour analyser un dossier client sensible. Les données avaient transité par des serveurs américains, soumis au [Cloud Act](https://www.cnil.fr/fr/le-cloud-act-americain-enjeux-et-risques).

Selon [Wolters Kluwer](https://www.wolterskluwer.com) (2025), **41% des cabinets d'avocats expriment des doutes sur la confidentialité de leurs données**.

Cette checklist : 12 points. Tous critiques. Tous vérifiables.

**Seuil de sécurité** : Minimum 10/12 en ✅ pour des données couvertes par le secret professionnel.

---

## Point 1 : Localisation de l'hébergement des données

Le [Cloud Act](https://www.cnil.fr/fr/le-cloud-act-americain-enjeux-et-risques) américain (2018) autorise les autorités US à accéder aux données hébergées par des entreprises US, **même stockées hors des États-Unis**.

| Réponse | Conformité |
|---------|-----------|
| Hébergement France (OVH, Scaleway) | ✅ Excellent |
| Hébergement UE | ✅ Bon |
| Hébergement USA | ❌ Risque Cloud Act |
| Pas de réponse claire | ❌ Red flag |

**Exemples** : ✅ [Doctrine](https://www.doctrine.fr), [Jimini AI](https://www.jimini.ai), Dalloz (France) · ❌ ChatGPT, Claude, [Harvey AI](https://www.harvey.ai) (USA)

---

## Point 2 : Réutilisation des données pour l'entraînement

Si l'outil utilise vos données clients pour entraîner ses modèles, vous violez le secret professionnel **et** le [RGPD](https://www.cnil.fr/fr/reglement-europeen-protection-donnees).

**Exemples** : ✅ [Doctrine](https://www.doctrine.fr), [Jimini AI](https://www.jimini.ai) (engagement contractuel) · ⚠ Claude (opt-out manuel) · ❌ ChatGPT gratuit

> **Exigez un engagement contractuel écrit de non-réutilisation des données.**

---

## Point 3 : Durée de conservation des données

Le [RGPD](https://www.cnil.fr/fr/reglement-europeen-protection-donnees) impose de ne conserver les données que le temps strictement nécessaire.

**Privilégiez** : suppression automatique (< 30 jours) ou suppression sur demande (< 48h).

---

## Point 4 : Chiffrement des données

- ✅ TLS 1.3 en transit + AES-256 au repos = Excellent
- ❌ Pas de chiffrement = Inacceptable

---

## Point 5 : Authentification et contrôle d'accès

- ✅ 2FA obligatoire
- ✅ Gestion des rôles (admin, utilisateur, lecture seule)
- ✅ Logs d'audit complets

---

## Point 6 : Certifications de sécurité

| Certification | Niveau |
|--------------|--------|
| **SecNumCloud** (ANSSI) | 🏆 Excellent |
| **ISO 27001** | ✅ Bon |
| **SOC 2 Type II** | ✅ Acceptable |
| Aucune | ❌ |

**Exemples** : ✅ Lextenso/Dalloz (SecNumCloud) · ✅ [Doctrine](https://www.doctrine.fr) (ISO 27001) · ✅ [Harvey AI](https://www.harvey.ai) (SOC 2, mais USA)

---

## Point 7 : Transferts vers des sous-traitants

**Exigez** la liste des sous-traitants et leur localisation. Transferts hors UE sans clauses contractuelles types = ❌ Red flag.

---

## Point 8 : Conformité avec le règlement [AI Act](https://eur-lex.europa.eu/legal-content/FR/TXT/?uri=CELEX:32024R1689) (UE)

Le [règlement européen sur l'IA](https://eur-lex.europa.eu/legal-content/FR/TXT/?uri=CELEX:32024R1689), adopté en juillet 2024, impose : gouvernance des données, supervision humaine, documentation technique, transparence, traçabilité.

→ Approfondir : [L'EU AI Act : ce que les avocats doivent savoir](/articles/eu-ai-act-avocats)

---

## Point 9 : Politique de gestion des incidents

**Exigez** : notification rapide (< 48h) et plan de gestion des incidents formalisé.

---

## Point 10 : Propriété intellectuelle et confidentialité des prompts

Vos prompts peuvent contenir des informations stratégiques. **Vérifiez** qu'ils restent confidentiels et supprimables.

---

## Point 11 : Transparence sur les modèles IA utilisés

Certains outils "spécialisés" utilisent ChatGPT ou Claude en backend sans le dire. Vos données transitent alors par OpenAI (USA).

**Exigez une transparence totale sur la stack technique.**

---

## Point 12 : Formation et sensibilisation des utilisateurs

- ✅ Formation RGPD + IA obligatoire pour tous
- ✅ Charte d'utilisation signée
- ✅ Sessions de sensibilisation régulières

→ En savoir plus : [Les 7 erreurs fatales à éviter](/articles/7-erreurs-deploiement-ia)

---

## Synthèse : Votre checklist opérationnelle

| Point | Question clé | Réponse minimum |
|-------|-------------|-----------------|
| 1. Hébergement | Où sont mes données ? | France ou UE |
| 2. Réentraînement | Utilise mes données ? | Non (engagement écrit) |
| 3. Conservation | Combien de temps ? | < 30 jours |
| 4. Chiffrement | Transit et repos ? | Oui (TLS + AES-256) |
| 5. Authentification | 2FA disponible ? | Oui (et activé) |
| 6. Certifications | ISO 27001 / SecNumCloud ? | Au moins ISO 27001 |
| 7. Sous-traitants | Transferts à des tiers ? | Non ou UE uniquement |
| 8. AI Act | Conforme règlement UE ? | Oui ou en cours |
| 9. Incidents | Plan de gestion ? | Oui (notif < 48h) |
| 10. Prompts | Restent privés ? | Oui |
| 11. Modèles | Quel modèle IA ? | Transparence totale |
| 12. Formation | Équipes formées ? | Oui (tous) |

**Scoring** : 12/12 ✅ = Conforme toutes données · 10-11 = Quelques points à clarifier · 8-9 ⚠ = Précautions · < 8 ❌ = Risque élevé

---

## Conclusion

Prenez 2 heures pour vérifier ces 12 points. Ça peut vous éviter des années de problèmes.

→ Guide complet : [Le guide ultime de l'IA juridique pour avocats](/articles/guide-ultime-ia-juridique)
→ Choisir votre outil : [Comment choisir votre IA juridique en 5 étapes](/articles/choisir-ia-juridique-5-etapes)

---

**Sources :** [Légifrance](https://www.legifrance.gouv.fr), [CNIL](https://www.cnil.fr), [CNIL – Cloud Act](https://www.cnil.fr/fr/le-cloud-act-americain-enjeux-et-risques), [Conseil National des Barreaux](https://www.cnb.avocat.fr), [EUR-Lex – AI Act](https://eur-lex.europa.eu/legal-content/FR/TXT/?uri=CELEX:32024R1689)
`;

export const ChecklistRGPDPage = () => (
  <ArticleLayout
    seoTitle="Checklist RGPD IA juridique : 12 points clés"
    seoDescription="Checklist RGPD en 12 points pour avocats et juristes avant d'adopter une IA. Hébergement, chiffrement, certifications, AI Act."
    seoKeywords="checklist rgpd ia juridique, rgpd avocat ia, secret professionnel ia, cloud act avocat, conformite ia juridique, ai act avocat"
    canonicalUrl="https://gaius-tech.com/articles/checklist-rgpd-ia-juridique"
    alternateUrlFr="https://gaius-tech.com/articles/checklist-rgpd-ia-juridique"
    alternateUrlEn="https://gaius-tech.com/articles/gdpr-checklist-legal-ai"
    category="Do It Yourself"
    categoryColor="emerald"
    title="Les 12 points de vigilance avant d'adopter une IA juridique : la checklist RGPD et déontologique"
    subtitle="12 points critiques. Tous vérifiables. La checklist née des erreurs des autres."
    date="17 février 2026"
    dateISO="2026-02-17"
    readTime="15 min"
    content={content}
  />
);
